import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AvailableNotification, NotificationListItem } from '../../../types';
import { getAvailableFrequencies, getEntitySelectionType } from '../../../../../utils/entityTypeUtils';
import { BiChevronRight } from "react-icons/bi";
import { HiOutlineViewBoards, HiOutlineBriefcase, HiOutlineClipboardList } from "react-icons/hi";
import CustomTooltip from '../../../../../components/common/CustomTooltip';
import { useIntegrationStatus } from '../../../../../hooks/useIntegrationStatus';

interface NotificationTypeListProps {
  selectedNotificationKey: string | null;
  onSelect: (notification: AvailableNotification) => void;
}

const getSortOrder = (entityType: string): number => {
  const selectionType = getEntitySelectionType(entityType);
  const orderMap: Record<string, number> = {
    'strategy_map': 1,
    'work_item': 2,
    'sprint': 3,
    'task': 4,
  };
  return orderMap[selectionType || ''];
};

const getEntityIcon = (entityType: string): React.ReactNode => {
  const selectionType = getEntitySelectionType(entityType);

  switch (selectionType) {
    case 'strategy_map':
      return <HiOutlineViewBoards className="w-6 h-6 text-gray-600 dark:text-gray-400 flex-shrink-0" />;
    case 'work_item':
      return <HiOutlineBriefcase className="w-6 h-6 text-gray-600 dark:text-gray-400 flex-shrink-0" />;
    case 'sprint':
      return (
        <>
          <img src="/sprints_light.png" alt="Sprints" className="w-6 h-6 dark:hidden flex-shrink-0" />
          <img src="/sprints-dark.png" alt="Sprints" className="w-6 h-6 hidden dark:inline flex-shrink-0" />
        </>
      );
    case 'task':
      return <HiOutlineClipboardList className="w-6 h-6 text-gray-600 dark:text-gray-400 flex-shrink-0" />;
    default:
      return null;
  }
};

const NotificationTypeList: React.FC<NotificationTypeListProps> = ({
  selectedNotificationKey,
  onSelect
}) => {
  const {
    slackAvailableNotifications: availableNotifications = [] as AvailableNotification[]
  } = useSelector((state: any) => state.settings);

  // Check integration status - Sprint notifications only available for Azure
  const { isIntegrationActive } = useIntegrationStatus();
  const isAzureConnected = isIntegrationActive('azure');

  const items = useMemo<NotificationListItem[]>(() => {
    const mapped = availableNotifications.map((notification: AvailableNotification) => {
      const selectionType = getEntitySelectionType(notification.entity_type);
      // Disable task notifications (coming soon) and sprint notifications if Azure is not connected
      const isDisabled = selectionType === 'task' || (selectionType === 'sprint' && !isAzureConnected);
      return {
        notification,
        label: notification.name,
        description: notification.description,
        sortOrder: getSortOrder(notification.entity_type),
        isDisabled
      };
    });

    return mapped.sort((a: NotificationListItem, b: NotificationListItem) => a.sortOrder - b.sortOrder);
  }, [availableNotifications, isAzureConnected]);


  const buttonEl = (item: NotificationListItem) => {

    return (
      <button
        onClick={() => {
          if (!item.isDisabled)
            onSelect(item.notification)
        }}
        disabled={item.isDisabled}
        className={`w-full flex flex-row items-start justify-between p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white text-left
          dark:bg-gray-800 transition-all duration-200
          ${item.isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-primary-light dark:hover:border-primary-dark hover:shadow-md'}
          ${selectedNotificationKey === item.notification.key ? 'border-primary-light shadow-lg' : ''}
        `}
      >
        <div className="flex items-start gap-4 flex-1">
          <div className="flex-shrink-0 mt-0.5">
            {getEntityIcon(item.notification.entity_type)}
          </div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              {item.label}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {item.description}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
              Supports: {getAvailableFrequencies(item.notification).join(', ')} reports
            </span>
          </div>
        </div>

        <BiChevronRight className="w-6 h-6 text-light-text-secondary dark:text-gray-600 flex-shrink-0 mt-0.5" />
      </button>
    )
  }

  const tooltipButtonEl = (item: NotificationListItem) => {
    const selectionType = getEntitySelectionType(item.notification.entity_type);
    const entityPlural = selectionType === 'work_item'
      ? 'work items'
      : selectionType === 'task'
        ? 'tasks'
        : selectionType === 'sprint'
          ? 'sprints'
          : 'entities';

    const frequencies = getAvailableFrequencies(item.notification).join(', ');

    // Show specific message for Sprint when Azure is not connected
    let tooltipText: string;
    if (selectionType === 'sprint' && !isAzureConnected) {
      tooltipText = 'Sprint notifications are only available with Azure DevOps integration.\nPlease connect your Azure DevOps account to enable sprint reports.';
    } else {
      tooltipText = `Slack notifications for ${entityPlural} are coming soon.\nYou will be able to configure automated ${selectionType !== 'task' ? frequencies : ''} reports and alerts for ${entityPlural}.`;
    }

    return (
      <CustomTooltip title={tooltipText}>
        <div className="w-full">
          {buttonEl(item)}
        </div>
      </CustomTooltip>
    );
  }


  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        {items.map((item: NotificationListItem) => {
          return (
            <React.Fragment key={item.notification.key}>
              {item.isDisabled ? tooltipButtonEl(item) : buttonEl(item)}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationTypeList;